import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import ActiveValues from '$constants/ActiveValues';
import { useCreateEmptyInterviewMutation } from '$store/modules/interview';
import { ReactComponent as CircleDot } from '$icons/CircleDot.svg';
import AttachmentSummary from '$components/Positions/AttachmentsSummary/AttachmentsSummary';
import { UserType } from '$types/UserTypes';
import HoverTooltip from '$components/Tooltip/HoverTooltip';
import isNewCandidate from '$utils/isNewCandidate';
import dayjs from 'dayjs';
import { ReactComponent as Verified } from '$icons/uncolored/verified.svg';
import classes from './CandidateSummaryCard.module.css';
import computeLastJob from '../../../../utils/computeLastJob';
import capitalizedName from '../../../../utils/capitalizedName';
import CandidateVideosModal from '../../CandidateVideosModal/CandidateVideosModal';
import CandidateActionBtnLeft from '../CandidateActionBtns/CandidateActionBtnLeft';
import SwitchTypes from '../../../../constants/CandidatesSwitchTypes';
import HireConfirmModal from './HireConfirmModal/HireConfirmModal';
import CandidateActionBtnRightGraph from '../CandidateActionBtns/CandidateActionBtnRightGraph';
import {
  companiesApi,
  useLoadOnePositionQuery,
} from '../../../../store/modules/companies';
import type { CandidateData } from '../../../../types/candidates';
import { useOriginPath } from '../../../../hooks/use-origin-path';
import { useAppDispatch } from '../../../../hooks';
import {
  StatusCandidatePositionOptions,
  candidatesApi,
  useUpdateCandidatePositionStatusMutation,
  useRemoveCandidateFromPositionMutation,
} from '../../../../store/modules/candidates';
import { triggerSegmentOnCloseModalTrack } from '../../../../store/globals/LastPlayedVideo';

interface Props {
  jobPositionStatus: string;
  candidate: CandidateData;
  candidateIndex: number;
  positionId: number;
  setSwitchingId: Dispatch<SetStateAction<number | null>>;
}

const CandidateSummaryCardGraph: FC<Props> = ({
  jobPositionStatus,
  candidate,
  candidateIndex: i,
  positionId,
  setSwitchingId,
}) => {
  const [searchParams] = useSearchParams();
  const skillsParams = searchParams.get('Skills');
  const currentSearchForSkins = skillsParams ? skillsParams.split(',') : [];
  const [showHireModal, setHireModal] = useState<boolean>(false);
  const [dataModalVideo, setDataModalVideo] = useState<{
    isModalOpen: boolean;
    candidateId: number;
    positionId: number;
  }>({ candidateId: candidate.Id, positionId, isModalOpen: false });

  const [lastJob, setLastJob] = useState<{
    title: string;
    company: null | string;
  }>({
    title: '',
    company: null,
  });
  const [t, i18n] = useTranslation('global');
  const { setOriginPath } = useOriginPath();
  const cps = useMemo(() => searchParams.get('cps') ?? 'new', [searchParams]);

  const { data } = useLoadOnePositionQuery({
    id: positionId,
  });
  const mappedSkills = data?.Skills_position.map((skill) => skill.Title);
  const dispatch = useAppDispatch();
  const [removeCandidateFromPosition] =
    useRemoveCandidateFromPositionMutation();

  const status = useMemo(() => {
    return {
      offerSent: t('CandidatePositionSummaryCard.offerSent'),
      requestInterview: t('CandidatePositionSummaryCard.requestInterview'),
    }[cps ?? ''];
  }, [cps, t]);
  const showStatus = candidate?.CandidatesPositionStatus?.[0]?.Status !== 'new';

  const [createEmptyInterview] = useCreateEmptyInterviewMutation();

  const [updateCandidatePositionStatus] =
    useUpdateCandidatePositionStatusMutation();

  useEffect(() => {
    setHireModal(false);
  }, [candidate]);

  const handleCloseModal = () => {
    dispatch(triggerSegmentOnCloseModalTrack());
    setDataModalVideo({ ...dataModalVideo, isModalOpen: false });
  };

  const handleShowHireModal = (show: boolean) => {
    setHireModal(show);
  };

  const handleRequestInterview = async () => {
    createEmptyInterview({
      candidatePositionStatusId: candidate.CandidatesPositionStatus[0].Id,
    }).then((newInterviewData) => {
      if ('error' in newInterviewData) return;
      dispatch(companiesApi.util.invalidateTags(['PositionsList', 'Company']));
      dispatch(candidatesApi.util.invalidateTags(['Candidates']));
      setTimeout(() => {
        setSwitchingId(candidate.Id);
      }, 1000);
    });
  };

  const handleChangePositionStatus = async (
    newStatus: StatusCandidatePositionOptions
  ) => {
    await updateCandidatePositionStatus({
      data: {
        CandidatePositionStatusId: candidate?.CandidatesPositionStatus[0]?.Id,
        Status: newStatus,
      },
    });

    dispatch(companiesApi.util.invalidateTags(['PositionsList']));
    dispatch(candidatesApi.util.invalidateTags(['Candidates']));
    setSwitchingId(candidate.Id);
  };

  const handleRemoveFromPosition = async () => {
    removeCandidateFromPosition({
      data: {
        candidateId: candidate.Id,
        positionId,
      },
      lang: i18n.language as 'es' | 'en',
    }).then(() => {
      dispatch(companiesApi.util.invalidateTags(['PositionsList']));
    });
    setTimeout(() => {
      setSwitchingId(candidate.Id);
    }, 1000);
  };

  useEffect(() => {
    if (
      candidate.CandidateWorkExperience &&
      candidate.CandidateWorkExperience.length
    ) {
      const lj = computeLastJob(candidate.CandidateWorkExperience);
      setLastJob(lj);
    } else {
      setLastJob({
        title: 'N/A',
        company: null,
      });
    }
  }, [candidate.CandidateWorkExperience]);

  const validStatus = ['new', 'offerSent', 'requestInterview', 'shortlisted'];

  const isValidStatus = validStatus.includes(cps);
  const isPositionClosed = jobPositionStatus === 'Closed';

  const handleName = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return capitalizedName(`${firstName} ${lastName}`);
    }
    if (firstName) {
      return capitalizedName(`${firstName}`);
    }
    if (lastName) {
      return capitalizedName(`${lastName}`);
    }

    return '';
  };

  return (
    <li className={classes.CandidateCard}>
      <CandidateVideosModal
        modalIsOpen={dataModalVideo.isModalOpen && candidate.Videos !== null}
        handleClose={handleCloseModal}
        candidateId={dataModalVideo.candidateId}
        positionId={dataModalVideo.positionId}
        additionalVideos={candidate.AdditionalVideos}
      />
      {Boolean(candidate.CandidatesPositionStatus) && isValidStatus && (
        <HireConfirmModal
          modalIsOpen={showHireModal}
          handleClose={handleShowHireModal}
          candidatePositionStatusId={0}
          positionId={positionId}
          candidateArrayIndex={i}
          candidateId={candidate.Id}
          setSwitchingId={setSwitchingId}
        />
      )}
      <div className={`${classes.leftCard} w-full`}>
        <header className={`${classes.Header} gap-2`}>
          <div className={`${classes.HeaderLeft} gap-2`}>
            <div className={`${classes.ImageContainer} w-14`}>
              <DefaultIcon2
                picture={candidate.User?.ProfilePicture?.Url}
                fontSize="text-3xl"
                firstName={candidate.User?.FirstName}
                lastName={candidate.User?.LastName}
              />
            </div>
            <div className={`${classes.HeaderData}`}>
              <div>
                <div className={`${classes.HeaderDataLeft} items-center`}>
                  <Link
                    role="button"
                    className="text-xl"
                    data-testid="profile-link"
                    to={`/simera-dashboard/positions/${positionId}/candidates/${candidate.Id}`}
                    onClick={() => {
                      setOriginPath(
                        `${window.location.pathname}?${searchParams}`
                      );
                    }}
                    aria-hidden
                    target="_blank"
                  >
                    {handleName(
                      candidate.User?.FirstName,
                      candidate.User?.LastName
                    )}
                  </Link>
                  <Link
                    role="button"
                    className={classes.ProfileLink}
                    data-testid="profile-link"
                    to={`/simera-dashboard/positions/${positionId}/candidates/${candidate.Id}`}
                    onClick={() => {
                      setOriginPath(
                        `${window.location.pathname}?${searchParams}`
                      );
                    }}
                    aria-hidden
                    target="_blank"
                  >
                    {t('CandidatePositionSummaryCard.ViewProfile')}
                  </Link>
                  <a
                    className={classes.ProfileLink}
                    href={`${process.env.REACT_APP_SPK_URL}/spk/${candidate.Id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('CandidatePositionSummaryCard.ViewSPK')}
                  </a>
                </div>
              </div>
              <div className={classes.CandidateHeaderOtherData}>
                <div className={`${classes.Row} flex items-center gap-2`}>
                  <div className={classes.Chip}>
                    <p className={classes.ChipText}>{`ID: ${candidate.Id}`}</p>
                  </div>
                  {showStatus && status && (
                    <div
                      className={classes.Chip}
                      style={{
                        backgroundColor: 'var(--primary-green)',
                      }}
                    >
                      <p className={classes.ChipText}>{status}</p>
                    </div>
                  )}
                  {!showStatus && isNewCandidate(candidate.CreateTime) && (
                    <div
                      className={classes.Chip}
                      style={{
                        backgroundColor: 'var(--primary-green)',
                      }}
                    >
                      <p className={classes.ChipText}>
                        {t('CandidatePositionSummaryCard.NewCandidate')}
                      </p>
                    </div>
                  )}
                  {isNewCandidate(
                    candidate.CandidatesPositionStatus?.[0]?.CreateTime
                  ) && (
                    <div
                      className={`${classes.ChipContainer} ${classes.ChipInfo} `}
                    >
                      <p className={classes.ChipText}>
                        {t('CandidatePositionSummaryCard.RecentlyAdded')}
                      </p>
                    </div>
                  )}
                  {candidate.CandidatesPositionStatus?.[0]?.CreateTime && (
                    <HoverTooltip
                      text={t('CandidatePositionSummaryCard.TooltipText')}
                    >
                      <div
                        className={classes.Date}
                        aria-hidden
                        data-testid="create-date"
                      >
                        {dayjs(
                          candidate.CandidatesPositionStatus?.[0]?.CreateTime
                        ).format('MM/DD/YYYY')}
                      </div>
                    </HoverTooltip>
                  )}
                </div>
              </div>
            </div>
            {/* <div className="pointer-default flex flex-row h-full">
              <div className="group relative font-semibold items-center justify-end flex gap-2 text-tags-green20 bg-tags-green10 rounded-xl px-4">
                <span className="hidden md:inline">Demand Score</span>
                <span className="text-2xl">{candidate.Score}</span>
              </div>
            </div> */}
          </div>
          <div className={classes.RightContent}>
            <div className={`${classes.ActionBtnsContainer} flex gap-2`}>
              {isValidStatus &&
                !isPositionClosed &&
                candidate?.CandidatesPositionStatus && (
                  <CandidateActionBtnLeft
                    activeValue={cps as ActiveValues}
                    handleHireModal={handleShowHireModal}
                    handleRequestInterview={handleRequestInterview}
                    handleChangePositionStatus={handleChangePositionStatus}
                    handleRemoveFromPosition={handleRemoveFromPosition}
                    candidateId={candidate.Id}
                  />
                )}
              {cps !== SwitchTypes.HIRED ? (
                <CandidateActionBtnRightGraph
                  candidate={candidate}
                  positionId={positionId}
                  salaryUpperBound={data?.SalaryUpperBound ?? 0}
                  candidateArrayIndex={i}
                  candidatePositionStatus={cps}
                  setSwitchingId={setSwitchingId}
                />
              ) : null}
            </div>
          </div>
        </header>
        <div className="flex flex-row gap-8">
          <div>
            <p className="text-sm">
              {t('CandidatePositionSummaryCard.Experience')}
            </p>
            <p className="font-bold text-base">
              {candidate.CandidateWorkExperience &&
                `${candidate.YearsOfExperience ?? 0} years +`}
            </p>
          </div>
          <div>
            <p className="text-sm">
              {t('CandidatePositionSummaryCard.SalaryRange')}
            </p>
            <p className="font-bold text-base">
              {candidate.CandidateJobInterest &&
              candidate.CandidateJobInterest?.SalaryLowerBound &&
              candidate.CandidateJobInterest?.SalaryUpperBound
                ? `$${candidate.CandidateJobInterest.SalaryLowerBound} - $${candidate.CandidateJobInterest.SalaryUpperBound}`
                : '$'}
            </p>
          </div>
          <div>
            <p className="text-sm">
              {t('CandidatePositionSummaryCard.LastJob')}
            </p>
            <p className="font-bold text-base flex flex-row items-center">
              {lastJob?.title || 'N/A'}
              {lastJob?.company && (
                <CircleDot fill="green" width="4" height="4" className="mx-2" />
              )}
              {lastJob?.company}
            </p>
          </div>
        </div>
        <hr className="my-2" />
        <div className={classes.ContentContainer}>
          <div className={classes.leftContent}>
            <div className={classes.LeftContentTop}>
              <p className={`${classes.ExtraBold} mb-2`}>Top Skills:</p>
              <div className="flex flex-row flex-wrap gap-2">
                {candidate.IsSimeraAICertified && (
                  <div className={`${classes.Chip} ${classes.ChipsSkills}`}>
                    <p className={`${classes.ChipText} flex flex-row gap-2`}>
                      <span>Simera AI Certified</span>
                      {candidate.IsSimeraAICertified && (
                        <Verified className="w-5 h-5 fill-internal-main" />
                      )}
                    </p>
                  </div>
                )}
                {candidate.CandidateSkills?.map((skill) => (
                  <div
                    key={`${candidate.Id}_${skill?.Skill?.Title}_${jobPositionStatus}`}
                    className={`${classes.Chip} ${
                      mappedSkills?.includes(skill.Skill?.Title)
                        ? classes.ChipsSkillsActive
                        : classes.ChipsSkills
                    } ${
                      currentSearchForSkins?.includes(
                        skill.Skill?.Title.trim().toLowerCase()
                      ) &&
                      !mappedSkills?.includes(skill.Skill?.Title) &&
                      classes.ChipsSkillsMatch
                    }`}
                  >
                    <p className={`${classes.ChipText} flex flex-row gap-2`}>
                      <span>{skill?.Skill?.Title}</span>
                      {skill.IsVerified && (
                        <Verified
                          className={`w-5 h-5 ${
                            !mappedSkills?.includes(skill.Skill?.Title)
                              ? 'fill-internal-main'
                              : 'fill-green-200'
                          } `}
                        />
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <AttachmentSummary
              role={UserType.INTERNAL}
              positionId={positionId}
              candidate={candidate}
            />
          </div>
        </div>
      </div>
    </li>
  );
};

export default CandidateSummaryCardGraph;
